<template>
  <div class="rental-edit">
    <main class="content">
      <!-- 信息完善通知 -->
      <div class="info-prompt" v-if="!profileCompleted">
        <div class="prompt-content">
          <p class="prompt-text">房东信息完善可以提升房源曝光度</p>
        </div>
        <button v-if="canEditProfile" class="prompt-btn" @click="showProfilePopup = true">去完善</button>
        <button v-else class="prompt-btn prompt-btn-disabled" @click="onDisabledClick">去完善</button>
      </div>

      <!-- 房源基本信息 -->
      <div class="form-section">
        <h2 class="section-title">房源基本信息</h2>
        <form class="form-content">
          <!-- 房源地址 -->
          <div class="address-row">
            <van-icon name="location" class="address-icon" />
            <span class="address-text">{{ form.address || '加载中...' }}</span>
          </div>

          <!-- 租金范围 -->
          <div class="rent-row">
            <div class="rent-item">
              <label class="form-label">最低租金 (元/月)</label>
              <input
                type="text"
                inputmode="numeric"
                v-model="form.minRent"
                placeholder="最低租金"
                class="form-input"
                maxlength="7"
              />
            </div>
            <div class="rent-item">
              <label class="form-label">最高租金 (元/月)</label>
              <input
                type="text"
                inputmode="numeric"
                v-model="form.maxRent"
                placeholder="最高租金"
                class="form-input"
                maxlength="7"
              />
            </div>
          </div>

          <!-- 联系电话 -->
          <div class="form-item">
            <label class="form-label">联系电话</label>
            <input
              type="tel"
              v-model="form.landlordMobile"
              placeholder="请输入联系电话"
              class="form-input"
              maxlength="11"
            />
          </div>

          <!-- 房源描述 -->
          <div class="form-item">
            <label class="form-label">房源描述</label>
            <div class="textarea-wrap">
              <textarea
                v-model="form.descriptionZh"
                placeholder="请输入房源描述"
                maxlength="800"
                class="form-textarea"
              ></textarea>
              <button class="voice-btn">
                <van-icon name="microphone" />
              </button>
              <span class="textarea-count">{{ (form.descriptionZh || '').length }}/800</span>
            </div>
          </div>
        </form>
      </div>

      <!-- 房源图片 -->
      <div class="form-section">
        <h2 class="section-title">房源图片</h2>
        <div class="image-list">
          <div class="image-item" v-for="(img, idx) in form.images" :key="idx">
            <img :src="getImageStream(img)" alt="房源图">
            <button class="image-del" @click="removeImage(idx)">
              <van-icon name="cross" />
            </button>
          </div>
          <div class="image-add" v-if="form.images.length < 9" @click="uploadImage">
            <van-icon name="plus" class="add-icon" />
            <span class="add-text">添加图片</span>
          </div>
        </div>
      </div>

      <!-- 更多内容区域 -->
      <div class="more-section">
        <!-- 房源标签 -->
        <div class="form-section">
          <h2 class="section-title">房源标签</h2>
          <div class="tag-list">
            <button
              v-for="tag in tags"
              :key="tag"
              :class="['tag-btn', { active: selectedTags.includes(tag) }]"
              @click="toggleTag(tag)"
            >{{ tag }}</button>
          </div>
        </div>

        <!-- 房型选择 -->
        <div class="form-section">
          <h2 class="section-title">房型选择</h2>
          <div class="tag-list">
            <button
              v-for="roomType in roomTypes"
              :key="roomType"
              :class="['tag-btn', { active: selectedRoomType === roomType }]"
              @click="selectRoomType(roomType)"
            >{{ roomType }}</button>
          </div>
        </div>

        <!-- 房间设施 -->
        <div class="form-section">
          <div class="section-header">
            <h2 class="section-title">房间设施</h2>
            <span class="section-tip">点击选择家具、设施</span>
          </div>
          <div class="facility-grid">
            <div
              v-for="facility in facilities"
              :key="facility.name"
              :class="['facility-item', { active: selectedFacilities.includes(facility.name) }]"
              @click="toggleFacility(facility.name)"
            >
              <div class="icon-wrap">
                <IconPark v-if="facility.iconName" class="facility-icon" :type="facility.iconName" theme="outline" size="24" :fill="selectedFacilities.includes(facility.name) ? '#4080ff' : '#666'" />
                <svg v-else class="facility-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 19v-7" />
                </svg>
                <van-icon v-if="selectedFacilities.includes(facility.name)" name="check" class="check-mark" />
              </div>
              <span class="facility-name">{{ facility.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 保存按钮 -->
      <button class="submit-btn" @click="onSave">保存修改</button>
    </main>

    <!-- 快捷信息弹窗 -->
    <van-popup v-model="showProfilePopup" position="center" :style="{width: '85%', maxWidth: '400px'}">
      <div class="popup-content">
        <div class="popup-header">
          <h3 class="popup-title">快捷信息录入</h3>
          <button class="popup-close" @click="showProfilePopup = false">
            <van-icon name="cross" />
          </button>
        </div>

        <div class="popup-form">
          <!-- 头像 -->
          <div class="popup-item">
            <label class="popup-label">头像</label>
            <div class="avatar-row">
              <img :src="getImageStream(profileFields.avatar || 'files/wx/images/jmgg/headerDefault.png')" class="avatar-preview" @click="uploadAvatar" />
            </div>
          </div>

          <!-- 工作单位 -->
          <div class="popup-item">
            <label class="popup-label">工作单位</label>
            <input type="text" v-model="profileFields.company" placeholder="请输入工作单位" class="popup-input" maxlength="50" />
          </div>

          <!-- 房东类型 -->
          <div class="popup-item">
            <label class="popup-label">类型</label>
            <span v-if="landlordTags.length === 0" class="tag-empty">暂无可选项</span>
            <van-checkbox-group v-else v-model="selectedLandlordTags">
              <van-checkbox
                v-for="tag in landlordTags"
                :key="tag.id"
                :name="tag.id"
                class="landlord-tag-item"
              >{{ tag.name }}</van-checkbox>
            </van-checkbox-group>
          </div>


          <!-- 车辆信息 -->
          <div class="popup-item">
            <label class="popup-label">车辆信息</label>
            <div class="car-toggle">
              <van-checkbox v-model="carForm.noCar" @change="onNoCarChange">无车辆</van-checkbox>
              <van-button v-if="!carForm.noCar" size="mini" type="info" @click="addCar">+ 添加车辆</van-button>
            </div>
          </div>

          <template v-if="!carForm.noCar">
            <div v-for="(car, idx) in carForm.list" :key="idx" class="car-card">
              <div class="car-card-header" @click="car.expanded = !car.expanded">
                <span>车辆 {{ idx + 1 }}</span>
                <span class="car-card-summary" v-if="!car.expanded && car.carNumber">{{ car.carNumber }}</span>
                <van-icon :name="car.expanded ? 'arrow-up' : 'arrow-down'" class="car-card-arrow" />
                <van-button size="mini" type="danger" plain @click.stop="removeCar(idx)">删除</van-button>
              </div>
              <div v-show="car.expanded" class="car-card-body">
                <div class="popup-item">
                  <label class="popup-label"><span class="required">*</span>车牌号码</label>
                  <input type="text" v-model="car.carNumber" placeholder="请输入车牌号码" class="popup-input" maxlength="8" />
                </div>
                <div class="popup-item">
                  <label class="popup-label"><span class="required">*</span>车辆类型</label>
                  <select v-model="car.carType" class="popup-input">
                    <option value="">请选择车辆类型</option>
                    <option v-for="t in carTypeList" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </div>
                <div class="popup-item">
                  <label class="popup-label">车辆品牌</label>
                  <input type="text" v-model="car.brand" placeholder="请输入车辆品牌" class="popup-input" maxlength="20" />
                </div>
                <div class="popup-item">
                  <label class="popup-label">车牌类型</label>
                  <select v-model="car.licencePlateType" class="popup-input">
                    <option value="">请选择车牌类型</option>
                    <option v-for="t in plateTypeList" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </div>
                <div class="popup-item">
                  <label class="popup-label">车牌颜色</label>
                  <select v-model="car.licencePlateColor" class="popup-input">
                    <option value="">请选择车牌颜色</option>
                    <option v-for="t in plateColorList" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </div>
                <div class="popup-item">
                  <label class="popup-label">车身颜色</label>
                  <select v-model="car.carColor" class="popup-input">
                    <option value="">请选择车身颜色</option>
                    <option v-for="t in carColorList" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </div>
              </div>
            </div>
          </template>

        </div>

        <button class="popup-submit" @click="saveProfile">确认提交</button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { getImageStream } from '@/utils/index'
import { IconPark } from '@icon-park/vue/es/all'

export default {
  components: {
    IconPark
  },
  data() {
    return {
      isEdit: false,
      showProfilePopup: false,
      canEditProfile: false,
      profileCompleted: true,
      missingFields: [],
      profileFields: {
        avatar: '',
        company: ''
      },
      carForm: {
        noCar: false,
        list: []
      },
      carTypeList: [],
      plateTypeList: [],
      plateColorList: [],
      carColorList: [],
      tags: [],
      tagsDict: [],
      selectedTags: [],
      roomTypes: [],
      roomTypesDict: [],
      selectedRoomType: '',
      facilities: [],
      facilitiesDict: [],
      selectedFacilities: [],
      landlordTags: [],
      selectedLandlordTags: [],
      form: {
        id: '',
        buildingId: '',
        subarea: '',
        community: '',
        address: '',
        minRent: '',
        maxRent: '',
        landlordMobile: '',
        descriptionZh: '',
        images: []
      }
    }
  },
  created () {
    this.form = {...this.form, ...this.$route.query}
    const id = this.$route.query.id
    if (id) {
      this.isEdit = true
      this.loadDetail(id)
    }
    this.checkProfile()
    this.loadTags()
    this.loadFacilities()
    this.loadRoomTypes()
    this.loadCarType()
    this.loadPlateType()
    this.loadPlateColor()
    this.loadCarColor()
    this.loadSpecialTags()
  },
  methods: {
    getImageStream,
    loadTags () {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_tag' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.tagsDict = data.dicts || []
          this.tags = this.tagsDict.map(item => item.label)
        }
      }).catch(() => {
      })
    },
    loadFacilities () {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_facility' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.facilitiesDict = data.dicts || []
          this.facilities = this.facilitiesDict.map(item => {
            let iconName = ''
            const ext = item.extended
            if (ext) {
              try {
                const parsed = JSON.parse(ext)
                iconName = (parsed && parsed.icon) || ''
              } catch (e) {
                iconName = ext
              }
            }
            return { name: item.label, iconName }
          })
        }
      }).catch(() => {
      })
    },
    loadRoomTypes () {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_house_type' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.roomTypesDict = data.dicts || []
          this.roomTypes = this.roomTypesDict.map(item => item.label)
        }
      }).catch(() => {
      })
    },
    loadCarType () {
      this.$http({
        url: this.$http.adornUrl('/wxapp/sys/dict/virtual'),
        method: 'GET',
        params: this.$http.adornParams({ code: 'carType' })
      }).then(({ data }) => {
        if (data && data.code === 0) this.carTypeList = data.virtualDicts
      })
    },
    loadPlateType () {
      this.$http({
        url: this.$http.adornUrl('/wxapp/sys/dict/virtual'),
        method: 'GET',
        params: this.$http.adornParams({ code: 'licencePlateType' })
      }).then(({ data }) => {
        if (data && data.code === 0) this.plateTypeList = data.virtualDicts
      })
    },
    loadPlateColor () {
      this.$http({
        url: this.$http.adornUrl('/wxapp/sys/dict/virtual'),
        method: 'GET',
        params: this.$http.adornParams({ code: 'licencePlateColor' })
      }).then(({ data }) => {
        if (data && data.code === 0) this.plateColorList = data.virtualDicts
      })
    },
    loadCarColor () {
      this.$http({
        url: this.$http.adornUrl('/wxapp/sys/dict/virtual'),
        method: 'GET',
        params: this.$http.adornParams({ code: 'carColor' })
      }).then(({ data }) => {
        if (data && data.code === 0) this.carColorList = data.virtualDicts
      })
    },
    addCar () {
      this.carForm.list.push({
        sId: null, carNumber: '', carType: '', brand: '',
        licencePlateType: '', licencePlateColor: '', carColor: '',
        expanded: true
      })
    },
    removeCar (idx) {
      const car = this.carForm.list[idx]
      this.$dialog.confirm({
        title: '确认删除',
        message: '确定删除该车辆吗？'
      }).then(() => {
        if (car && car.sId != null) {
          this.$toast.loading({ duration: 0, message: '删除中...', forbidClick: true })
          this.$http({
            url: this.$http.adornUrl('/wxapp/devicecar/delete'),
            method: 'POST',
            data: this.$http.adornData([Number(car.sId)], false)
          }).then(({ data }) => {
            this.$toast.clear()
            if (data && data.code === 0) {
              this.carForm.list.splice(idx, 1)
              this.$toast.success('删除成功')
            } else {
              this.$toast.fail((data && data.msg) || '删除失败')
            }
          }).catch(() => {
            this.$toast.clear()
            this.$toast.fail('删除失败')
          })
        } else {
          this.carForm.list.splice(idx, 1)
        }
      }).catch(() => {})
    },
    onNoCarChange () {
      if (this.carForm.noCar) {
        this.carForm.list = []
      }
    },
    uploadAvatar () {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.onchange = (e) => {
        const file = e.target.files[0]
        if (!file) return
        if (file.size > 2 * 1024 * 1024) {
          this.$toast('头像不能超过2MB')
          return
        }
        this.$toast.loading({ duration: 0, message: '上传中...', forbidClick: true })
        const formdata = new FormData()
        formdata.append('file', file)
        formdata.append('path', 'landlord/avatar')
        this.$httpCustApp({
          url: this.$httpCustApp.adornUrl('/base/file/file/upload'),
          method: 'post',
          data: formdata
        }).then(({ data }) => {
          this.$toast.clear()
          if (data.code === 0 && data.fileMessage) {
            this.profileFields.avatar = data.fileMessage.relativePath
          } else {
            this.$toast.fail('上传失败')
          }
        }).catch(() => {
          this.$toast.clear()
        })
      }
      input.click()
    },
    loadDetail (id) {
      this.$toast.loading({ duration: 0, message: '加载中...', forbidClick: true })
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/landlord/listing/info/' + id),
        method: 'get',
        params: this.$httpCustApp.adornParams({})
      }).then(({ data }) => {
        this.$toast.clear()
        if (data.code === 0 && data.listing) {
          const listing = data.listing
          this.form = {
            ...this.form,
            id: listing.id,
            buildingId: listing.buildingId,
            subarea: listing.subarea,
            community: listing.community || '',
            minRent: listing.minRent || '',
            maxRent: listing.maxRent || '',
            landlordMobile: listing.landlordMobile || '',
            descriptionZh: listing.descriptionZh || '',
            images: (listing.images || [])
          }
          if (listing.houseType != null) {
            const ht = this.roomTypesDict.find(d => parseInt(d.value, 10) === listing.houseType)
            if (ht) this.selectedRoomType = ht.label
          }
          if (listing.tagIds && listing.tagIds.length) {
            this.selectedTags = listing.tagIds
              .map(id => {
                const t = this.tagsDict.find(d => String(d.value) === String(id))
                return t ? t.label : null
              })
              .filter(Boolean)
          }
          if (listing.facilityIds && listing.facilityIds.length) {
            this.selectedFacilities = listing.facilityIds
              .map(id => {
                const f = this.facilitiesDict.find(d => String(d.value) === String(id))
                return f ? f.label : null
              })
              .filter(Boolean)
          }
        } else {
          this.$toast.fail({ duration: 1500, forbidClick: true, message: data.msg || '加载失败' })
        }
      }).catch(() => {
        this.$toast.clear()
      })
    },
    checkProfile () {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/landlord/listing/checkProfile'),
        method: 'get',
        params: this.$httpCustApp.adornParams({
          landlordId: this.$route.query.landlordId || this.$globalData.userInfo.userId
        })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.canEditProfile = true
          this.profileCompleted = data.completed !== false
          this.missingFields = data.missingFields || []

          if (data.headImg) {
            this.profileFields.avatar = data.headImg
          }
          if (data.company) {
            this.profileFields.company = data.company
          }
          if (data.noCar === 1) {
            this.carForm.noCar = true
          } else {
            this.loadCarList()
          }
        } else {
          this.canEditProfile = false
        }
      }).catch(() => {
        this.canEditProfile = false
      })
    },
    loadCarList () {
      const landlordId = this.$route.query.landlordId || this.$globalData.userInfo.userId
      this.$http({
        url: this.$http.adornUrl('/wxapp/devicecar/list'),
        method: 'GET',
        params: this.$http.adornParams({ userId: landlordId, page: 1, limit: 999 })
      }).then(({ data }) => {
        if (data && data.code === 0 && data.page && data.page.list) {
          const cars = data.page.list
          if (cars.length > 0) {
            this.carForm.noCar = false
            this.carForm.list = cars.map(c => ({
              sId: c.sid,
              carNumber: c.carNumber || '',
              carType: c.type || '',
              brand: c.brand || '',
              licencePlateType: c.licencePlateType || '',
              licencePlateColor: c.licencePlateColor || '',
              carColor: c.color || '',
              expanded: false
            }))
          } else {
            this.carForm.list = []
          }
        }
      })
    },
    loadSpecialTags () {
      const userId = this.$route.query.landlordId || this.$globalData.userInfo.userId
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/landlord/specialTags'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ userId })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.landlordTags = data.list || []
          this.selectedLandlordTags = this.landlordTags
            .filter(t => t.selected)
            .map(t => t.id)
        }
      })
    },
    uploadImage () {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.multiple = false
      input.onchange = (e) => {
        const file = e.target.files[0]
        if (!file) return
        if (file.size > 5 * 1024 * 1024) {
          this.$toast('图片不能超过5MB')
          return
        }
        this.$toast.loading({ duration: 0, message: '上传中...', forbidClick: true })
        const formdata = new FormData()
        formdata.append('file', file)
        formdata.append('path', 'landlord/listing')
        this.$httpCustApp({
          url: this.$httpCustApp.adornUrl('/base/file/file/upload'),
          method: 'post',
          data: formdata
        }).then(({ data }) => {
          this.$toast.clear()
          if (data.code === 0 && data.fileMessage) {
            this.form.images.push(data.fileMessage.relativePath)
          } else {
            this.$toast.fail({ duration: 1500, forbidClick: true, message: data.msg || '上传失败' })
          }
        }).catch(() => {
          this.$toast.clear()
        })
      }
      input.click()
    },
    removeImage (idx) {
      this.form.images.splice(idx, 1)
    },
    onSave () {
      if (!this.form.minRent) {
        this.$toast('请输入最低租金')
        return
      }
      if (!this.form.maxRent) {
        this.$toast('请输入最高租金')
        return
      }
      if (!this.form.landlordMobile) {
        this.$toast('请输入联系电话')
        return
      }
      if (Number(this.form.maxRent) <= Number(this.form.minRent)) {
        this.$toast('最高租金必须大于最低租金')
        return
      }
      if (!this.form.descriptionZh) {
        this.$toast('请输入房源描述')
        return
      }
      this.$toast.loading({ duration: 0, message: '保存中...', forbidClick: true })

      const tagIds = this.tagsDict
        .filter(item => this.selectedTags.includes(item.label))
        .map(item => item.value)

      let houseType = null
      if (this.selectedRoomType) {
        const roomTypeItem = this.roomTypesDict.find(item => item.label === this.selectedRoomType)
        if (roomTypeItem) {
          houseType = parseInt(roomTypeItem.value, 10)
        }
      }

      const facilityIds = []
      this.selectedFacilities.forEach(facility => {
        const facilityItem = this.facilitiesDict.find(item => item.label === facility)
        if (facilityItem) {
          facilityIds.push(facilityItem.value)
        }
      })

      const params = {
        descriptionZh: this.form.descriptionZh,
        landlordMobile: this.form.landlordMobile,
        minRent: this.form.minRent,
        maxRent: this.form.maxRent,
        images: this.form.images,
        tagIds,
        facilityIds,
        houseType
      }
      if (this.form.id) params.id = this.form.id
      if (this.form.buildingId) params.buildingId = this.form.buildingId

      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/landlord/listing/save'),
        method: 'post',
        data: this.$httpCustApp.adornData(params)
      }).then(({ data }) => {
        this.$toast.clear()
        if (data.code === 0) {
          this.$toast.success('保存成功')
          setTimeout(() => {
            this.$router.go(-1)
          }, 1000)
        } else {
          this.$toast.fail({ duration: 1500, forbidClick: true, message: data.msg || '保存失败' })
        }
      }).catch(() => {
        this.$toast.clear()
      })
    },
    saveProfile () {
      const landlordId = this.$route.query.landlordId || this.$globalData.userInfo.userId
      const self = this

      if (!this.carForm.noCar) {
        for (let i = 0; i < this.carForm.list.length; i++) {
          const car = this.carForm.list[i]
          if (!car.carNumber) {
            this.$toast.fail('请输入车牌号码')
            return
          }
          if (!car.carType) {
            this.$toast.fail('请选择车辆类型')
            return
          }
        }
      }

      this.$toast.loading({ duration: 0, message: '提交中...', forbidClick: true })

      const doSave = function () {
        const promises = []
        promises.push(self.$httpCustApp({
          url: self.$httpCustApp.adornUrl('/wxapp/landlord/listing/saveProfile'),
          method: 'post',
          data: self.$httpCustApp.adornData({
            landlordId: landlordId,
            headImg: self.profileFields.avatar || undefined,
            company: self.profileFields.company || undefined,
            noCar: self.carForm.noCar ? 1 : 0
          })
        }))

        if (!self.carForm.noCar) {
          self.carForm.list.forEach(function (car) {
            if (car.carNumber) {
              promises.push(self.$http({
                url: self.$http.adornUrl('/wxapp/devicecar/save'),
                method: 'POST',
                data: self.$http.adornData({
                  userId: landlordId,
                  subarea: self.form.subarea || undefined,
                  buildingId: self.form.buildingId || undefined,
                  carNumber: car.carNumber,
                  type: car.carType || undefined,
                  brand: car.brand || undefined,
                  licencePlateType: car.licencePlateType || undefined,
                  licencePlateColor: car.licencePlateColor || undefined,
                  color: car.carColor || undefined,
                  temporary: 0,
                  createUserId: landlordId
                })
              }))
            }
          })
        }

        // 房东特殊标签绑定
        var communityId = self.form.community
        if (communityId) {
          var selectedSet = new Set(self.selectedLandlordTags.map(Number))
          self.landlordTags.forEach(function (tag) {
            if (selectedSet.has(tag.id)) {
              promises.push(self.$httpCustApp({
                url: self.$httpCustApp.adornUrl('/wxapp/landlord/saveLabel'),
                method: 'POST',
                params: self.$httpCustApp.adornParams({
                  community: communityId,
                  userId: landlordId,
                  labelId: tag.id
                })
              }))
            } else {
              promises.push(self.$httpCustApp({
                url: self.$httpCustApp.adornUrl('/wxapp/landlord/removeLabel'),
                method: 'POST',
                params: self.$httpCustApp.adornParams({
                  community: communityId,
                  userId: landlordId,
                  labelId: tag.id
                })
              }))
            }
          })
        }

        Promise.all(promises).then(function (results) {
          self.$toast.clear()
          var allOk = results.every(function (r) { return r.data && r.data.code === 0 })
          if (allOk) {
            self.showProfilePopup = false
            self.$toast.success('提交成功')
            self.checkProfile()
            self.loadSpecialTags()
          } else {
            var failed = results.find(function (r) { return !r.data || r.data.code !== 0 })
            self.$toast.fail({ duration: 1500, forbidClick: true, message: (failed && failed.data && failed.data.msg) || '提交失败' })
          }
        }).catch(function () {
          self.$toast.clear()
          self.$toast.fail({ duration: 1500, forbidClick: true, message: '提交失败' })
        })
      }

      // 无车辆：先删除已有车辆，再保存
      if (this.carForm.noCar) {
        this.$http({
          url: this.$http.adornUrl('/wxapp/devicecar/list'),
          method: 'GET',
          params: this.$http.adornParams({ userId: landlordId, page: 1, limit: 999 })
        }).then(function ({ data }) {
          if (data && data.code === 0) {
            var cars = (data.page && data.page.list) || []
            var sids = cars.map(function (c) { return c.sid })
            if (sids.length > 0) {
              self.$http({
                url: self.$http.adornUrl('/wxapp/devicecar/delete'),
                method: 'POST',
                data: self.$http.adornData(sids, false)
              }).then(function () {
                doSave()
              }).catch(function () {
                doSave()
              })
            } else {
              doSave()
            }
          } else {
            doSave()
          }
        }).catch(function () {
          doSave()
        })
      } else {
        doSave()
      }
    },
    onDisabledClick () {
      this.$toast('没有修改权限')
    },
    toggleTag (tag) {
      const index = this.selectedTags.indexOf(tag)
      if (index > -1) {
        this.selectedTags.splice(index, 1)
      } else {
        this.selectedTags.push(tag)
      }
    },
    selectRoomType (roomType) {
      this.selectedRoomType = roomType
    },
    toggleFacility (facility) {
      const index = this.selectedFacilities.indexOf(facility)
      if (index > -1) {
        this.selectedFacilities.splice(index, 1)
      } else {
        this.selectedFacilities.push(facility)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.rental-edit {
  min-height: 100vh;
  background: #f5f5f5;

  .content {
    padding: 22px 29px 144px;
  }

  .info-prompt {
    background: #fff2f0;
    border: 2px solid #ffccc7;
    border-radius: 14px;
    padding: 18px 22px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 29px;

    .prompt-content {
      flex: 1;
      min-width: 0;
    }

    .prompt-text {
      font-size: 23px;
      color: #cf1322;
      font-weight: 600;
    }

    .prompt-btn {
      background: #4080ff;
      color: #fff;
      font-size: 23px;
      font-weight: 700;
      padding: 9px 22px;
      border-radius: 45px;
      flex-shrink: 0;
      margin-left: 18px;
      transition: transform 0.15s ease;
      border-color: transparent;
      &:active {
        transform: scale(0.95);
      }
    }

    .prompt-btn-disabled {
      background: #c0c0c0;
      color: #fff;
    }
  }

  .form-section {
    background: #fff;
    border-radius: 18px;
    padding: 29px;
    margin-bottom: 29px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);

    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 25px;
    }

    .section-title {
      font-size: 28px;
      font-weight: 700;
      color: #4080ff;
      margin-bottom: 25px;
      margin-top: 0;
    }

    .section-header .section-title {
      margin-bottom: 0;
    }

    .section-tip {
      font-size: 21px;
      color: #999;
    }

    .form-content {
      display: flex;
      flex-direction: column;
      gap: 29px;
    }

    .address-row {
      display: flex;
      align-items: center;
      gap: 14px;
      color: #333;

      .address-icon {
        font-size: 28px;
        color: #4080ff;
      }

      .address-text {
        font-size: 25px;
      }
    }

    .rent-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 22px;

      .rent-item {
        display: flex;
        flex-direction: column;
      }
    }

    .form-item {
      display: flex;
      flex-direction: column;
    }

    .form-label {
      font-size: 25px;
      font-weight: 600;
      color: #333;
      margin-bottom: 11px;
    }

    .form-input {
      width: 100%;
      font-size: 25px;
      padding: 18px 22px;
      background: #f5f5f5;
      border: 2px solid #e5e5e5;
      border-radius: 11px;
      outline: none;
      box-sizing: border-box;

      &:focus {
        border-color: #4080ff;
        box-shadow: 0 0 0 4px rgba(64, 128, 255, 0.1);
      }
    }

    .textarea-wrap {
      position: relative;

      .form-textarea {
        width: 100%;
        min-height: 180px;
        font-size: 25px;
        padding: 18px 22px;
        padding-right: 80px;
        background: #f5f5f5;
        border: 2px solid #e5e5e5;
        border-radius: 11px;
        outline: none;
        resize: none;
        box-sizing: border-box;

        &:focus {
          border-color: #4080ff;
          box-shadow: 0 0 0 4px rgba(64, 128, 255, 0.1);
        }
      }

      .voice-btn {
        position: absolute;
        right: 18px;
        top: 18px;
        font-size: 32px;
        color: #4080ff;
        background: transparent;
        border: none;
      }

      .textarea-count {
        position: absolute;
        bottom: 18px;
        right: 18px;
        font-size: 22px;
        color: #999;
      }
    }

    .tag-list {
      display: flex;
      flex-wrap: wrap;
      gap: 18px;
    }

    .tag-btn {
      padding: 14px 29px;
      border: 2px solid #e5e5e5;
      border-radius: 45px;
      font-size: 25px;
      color: #333;
      background: #f5f5f5;
      transition: all 0.2s ease;

      &.active {
        background: #4080ff;
        color: #fff;
        border-color: #4080ff;
      }
    }

    .facility-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 36px 0;
      text-align: center;
    }

    .facility-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s ease;

      .icon-wrap {
        position: relative;
        width: 58px;
        height: 58px;
        display: flex;
        align-items: center;
        justify-content: center;

        .facility-icon {
          width: 50px;
          height: 50px;
          color: #999;
          transition: color 0.2s ease;
        }

        .check-mark {
          position: absolute;
          top: -8px;
          right: -8px;
          font-size: 24px;
          color: #4080ff;
        }
      }

      &.active {
        .facility-icon {
          color: #4080ff;
        }
      }

      .facility-name {
        font-size: 21px;
        color: #333;
        margin-top: 9px;
      }
    }
  }

  .image-list {
    display: flex;
    gap: 14px;
    overflow-x: auto;
    padding-bottom: 7px;

    &::-webkit-scrollbar {
      display: none;
    }

    .image-item {
      width: 108px;
      height: 108px;
      border-radius: 14px;
      overflow: hidden;
      position: relative;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .image-del {
        position: absolute;
        top: 4px;
        right: 4px;
        width: 32px;
        height: 32px;
        background: rgba(255, 59, 48, 0.9);
        color: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        border: none;
      }
    }

    .image-add {
      width: 108px;
      height: 108px;
      border: 2px dashed #d9d9d9;
      border-radius: 14px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #fafafa;
      flex-shrink: 0;

      .add-icon {
        font-size: 36px;
        color: #999;
      }

      .add-text {
        font-size: 21px;
        color: #999;
        margin-top: 4px;
      }
    }
  }

  .more-section {
    display: flex;
    flex-direction: column;
    gap: 29px;
  }

  .submit-btn {
    display: block;
    width: 100%;
    height: 86px;
    line-height: 86px;
    text-align: center;
    background: #4080ff;
    color: #fff;
    border-radius: 18px;
    font-size: 31px;
    font-weight: 700;
    box-shadow: 0 7px 14px rgba(64, 128, 255, 0.3);
    transition: transform 0.15s ease;
    border: none;

    &:active {
      transform: scale(0.98);
    }
  }

  .popup-content {
    background: #fff;
    border-radius: 25px;
    padding: 36px;

    .popup-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 36px;
    }

    .popup-title {
      font-size: 31px;
      font-weight: 700;
      color: #333;
      margin-top: 0;
    }

    .popup-close {
      font-size: 36px;
      color: #999;
      background: transparent;
      border: none;
    }

    .popup-form {
      display: flex;
      flex-direction: column;
      gap: 29px;
    }

    .popup-item {
      display: flex;
      flex-direction: column;
    }

    .popup-sep {
      height: 1px;
      background: #eee;
      margin: 8px 0 16px;
    }

    .car-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;

      ::v-deep .van-checkbox__label {
        font-size: 25px;
      }
    }

    .tag-empty {
      font-size: 23px;
      color: #999;
    }

    .landlord-tag-item {
      margin-bottom: 22px;

      ::v-deep .van-checkbox__label {
        font-size: 25px;
      }
    }

    .car-card {
      background: #f9fafb;
      border-radius: 11px;
      padding: 16px;
      margin-top: 14px;

      .car-card-header {
        display: flex;
        align-items: center;
        font-size: 23px;
        font-weight: 600;
        color: #333;
        cursor: pointer;

        .car-card-summary {
          flex: 1;
          font-size: 21px;
          font-weight: 400;
          color: #999;
          margin-left: 14px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .car-card-arrow {
          margin-left: auto;
          margin-right: 14px;
          font-size: 24px;
          color: #999;
        }
      }

      .car-card-body {
        margin-top: 12px;
      }

      .popup-item {
        margin-bottom: 12px;

        .popup-label {
          font-size: 22px;
          margin-bottom: 6px;
        }

        .popup-input {
          font-size: 22px;
          padding: 12px 16px;
        }
      }
    }

    .popup-label {
      font-size: 25px;
      font-weight: 600;
      color: #333;
      margin-bottom: 11px;

      .required {
        color: #ee0a24;
        margin-right: 4px;
      }
    }

    .avatar-row {
      display: flex;
      align-items: center;

      .avatar-preview {
        width: 80px;
        height: 80px;
        border-radius: 8px;
        object-fit: cover;
      }

      .avatar-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 80px;
        height: 80px;
        border-radius: 8px;
        border: 2px dashed #d9d9d9;
        background: #fafafa;
        gap: 6px;
      }
    }

    .popup-input {
      width: 100%;
      font-size: 25px;
      padding: 18px 22px;
      background: #f5f5f5;
      border: 2px solid #e5e5e5;
      border-radius: 11px;
      outline: none;
      box-sizing: border-box;
      appearance: none;

      &:focus {
        border-color: #4080ff;
        box-shadow: 0 0 0 4px rgba(64, 128, 255, 0.1);
      }

      &.disabled {
        background: #f0f0f0;
        color: #999;
        cursor: not-allowed;
      }
    }

    .radio-group {
      display: flex;
      gap: 36px;

      .radio-item {
        display: flex;
        align-items: center;
        gap: 9px;
        font-size: 25px;
        color: #333;
        cursor: pointer;

        input[type="radio"] {
          width: 28px;
          height: 28px;
          accent-color: #4080ff;
        }
      }
    }

    .popup-submit {
      width: 100%;
      height: 86px;
      line-height: 86px;
      text-align: center;
      background: #4080ff;
      color: #fff;
      border-radius: 14px;
      font-size: 31px;
      font-weight: 700;
      margin-top: 36px;
      border: none;
      transition: transform 0.15s ease;

      &:active {
        transform: scale(0.98);
      }
    }
  }
}
</style>
